package org.vreo.devicehub.features.userAuthentication.presentation.verification

import kotlin.time.ExperimentalTime

import org.koin.compose.viewmodel.koinViewModel
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.outlined.LockPerson
import androidx.compose.material.icons.outlined.Refresh
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource

import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.error_general_title
import devicehub.composeapp.generated.resources.verification_button_title
import devicehub.composeapp.generated.resources.verification_description
import devicehub.composeapp.generated.resources.verification_resend_button_title
import devicehub.composeapp.generated.resources.verification_title
import org.vreo.devicehub.features.core.presentation.timestampToDateAndTime


/**
 * Account verification screen root
 *
 * @param viewModel view model
 * @param onBackNavigationClick back navigation handler
 * @param onVerificationSuccess success handler, action taken on successful verification of the new user
 */
@Composable
fun AccountVerificationScreenRoot(
    viewModel: AccountVerificationViewModel = koinViewModel(),
    onBackNavigationClick: () -> Unit,
    onVerificationSuccess: (String) -> Unit
) {
    val state: AccountVerificationState by viewModel.state.collectAsStateWithLifecycle()

    AccountVerificationView(
        state = state,
        onAction = { action ->
            val actionRequest = when (action) {
                is AccountVerificationActions.OnBackNavigationClicked -> {
                    onBackNavigationClick()
                    action
                }

                is AccountVerificationActions.OnVerifyClicked -> {
                    action.copy(callback = onVerificationSuccess)
                }

                else -> action
            }

            viewModel.onAction(actionRequest)
        }
    )
}


/**
 * Account verification view, provides access to account verification ui
 *
 * @param state composable state, manages state of all fields
 * @param onAction action handle
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AccountVerificationView(
    state: AccountVerificationState,
    onAction: (AccountVerificationActions) -> Unit
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = {},
                navigationIcon = {
                    IconButton(
                        onClick = {
                            onAction(AccountVerificationActions.OnBackNavigationClicked())
                        }
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = null
                        )
                    }
                }
            )
        }
    ) { innerPadding ->
        Box(
            contentAlignment = Alignment.TopCenter,
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
        ) {
            // Main content
            Column(
                verticalArrangement = Arrangement.spacedBy(8.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier
                    .widthIn(max = 700.dp)
            ) {
                Icon(
                    imageVector = Icons.Outlined.LockPerson,
                    contentDescription = null,
                    modifier = Modifier
                        .size(140.dp)
                        .padding(16.dp)
                )

                Text(
                    text = stringResource(Res.string.verification_title),
                    style = MaterialTheme.typography.displaySmall.copy(
                        fontWeight = FontWeight.ExtraBold
                    )
                )

                Text(
                    text = stringResource(Res.string.verification_description),
                    style = MaterialTheme.typography.bodyMedium
                )

                Text(
                    text = state.userEmail,
                    style = MaterialTheme.typography.bodyMedium.copy(
                        fontWeight = FontWeight.Bold
                    ),
                    color = Color.Blue
                )

                Text(
                    text = "Valid until: ${formatDateAndTime(state.validUntilMs ?: 0)}",
                    style = MaterialTheme.typography.bodyMedium
                )

                ControlSection(
                    state = state,
                    onAction = onAction,
                    modifier = Modifier.padding(top = 24.dp)
                )
            }

            // Conditional section for displaying loading and error states
            if (state.isLoading) {
                LoadingDialog()
            } else if (state.error != null) {
                ErrorDialog(
                    dialogTitle = stringResource(Res.string.error_general_title),
                    dialogText = state.error.asString(),
                    onDismissRequest = {
                        onAction(AccountVerificationActions.OnClearError())
                    },
                    onConfirmation = {
                        onAction(AccountVerificationActions.OnClearError())
                    }
                )
            }
        }
    }
}

/**
 * Control section, provides composable to verify or resend verification code
 *
 * @param state composable state, manages state of all fields
 * @param onAction action handle
 * @param modifier modifier
 */
@Composable
private fun ControlSection(
    state: AccountVerificationState,
    onAction: (AccountVerificationActions) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        verticalArrangement = Arrangement.spacedBy(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = modifier
    ) {
        OutlinedTextField(
            value = state.verificationCode,
            onValueChange = {
                onAction(AccountVerificationActions.OnUpdateVerificationCode(it))
            },
            singleLine = true,
            modifier = Modifier.fillMaxWidth()
        )

        Button(
            onClick = {
                onAction(AccountVerificationActions.OnVerifyClicked())
            },
            modifier = Modifier.fillMaxWidth()
        ) {
            Text(text = stringResource(Res.string.verification_button_title))
        }

        IconButton(
            onClick = {
                onAction(AccountVerificationActions.OnResendVerificationCodeClicked())
            },
            modifier = Modifier.fillMaxWidth()
        ) {
            Row {
                Icon(
                    imageVector = Icons.Outlined.Refresh,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.tertiary
                )
                Text(
                    text = stringResource(Res.string.verification_resend_button_title),
                    color = MaterialTheme.colorScheme.tertiary
                )
            }
        }
    }
}

/**
 * Format date and time int string of HH:MM:SS
 *
 * @param timeStampMs unix timestamp in milliseconds
 * @return formated time string
 */
@OptIn(ExperimentalTime::class)
private fun formatDateAndTime(timeStampMs: Long): String {
    if (timeStampMs <= 0) return "N/A"

    return try {
        val dateTime = timestampToDateAndTime(timeStampMs)

        val hour = dateTime.hour
        val minute = dateTime.minute
        val second = dateTime.second

        val paddedHour = hour.toString().padStart(2, '0')
        val paddedMinute = minute.toString().padStart(2, '0')
        val paddedSecond = second.toString().padStart(2, '0')

        return "$paddedHour:$paddedMinute:$paddedSecond"

    } catch (_: Exception) {
        "N/A"
    }
}

@Preview
@Composable
fun AccountVerificationViewPreview() {
    MaterialTheme {
        AccountVerificationView(
            state = AccountVerificationState(
                userEmail = "demo@emial.com",
                validUntilMs = 1759412648858L
            ),
            onAction = {}
        )
    }
}